import { datadogRum } from '@datadog/browser-rum';
import { useQueryClient } from '@tanstack/react-query';
import qs from 'qs';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { AppLoader } from '@hero/components/atoms';
import { configs } from '@hero/configs';
import { PUNCH_OUT_SESSION, QUERY_KEYS } from '@hero/constants';
import { useQueries } from '@hero/hooks';
import { ContinueLogin } from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import { organizationServices } from '@hero/services';
import { useGetUser, useLoginWithCode } from '@hero/services/organization-service/hooks';
import { useNotification } from '@hero/store/hooks';
import { getRedirectUrl, sendPostMessage, storage } from '@hero/utils';

const NON_REDIRECT_PATHS = [
  ROUTE_PATHS.INVITE_DETAIL(),
  ROUTE_PATHS.INVITE_EXPIRED(),
  ROUTE_PATHS.SIGN_IN,
  ROUTE_PATHS.SIGN_OUT,
  ROUTE_PATHS.SIGN_UP,
  ROUTE_PATHS.UNDER_MAINTENANCE,
  ROUTE_PATHS.NOT_FOUND
];

export function AuthProvider({ children }) {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const { mutateAsync: sendCode } = useLoginWithCode();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showError } = useNotification();
  const { queries } = useQueries();

  const { refetch } = useGetUser({ enabled: false });

  async function startAuth() {
    setIsLoading(true);

    const redirectPage = configs.publicLogin.register ? ROUTE_PATHS.SIGN_UP : ROUTE_PATHS.SIGN_IN;

    //! CASE 0: if the register is disabled and the current path is the sign up page, then redirect to the sign in page
    if (!configs?.publicLogin?.register && location?.pathname === ROUTE_PATHS.SIGN_UP) {
      navigate(ROUTE_PATHS.SIGN_IN, { replace: true });
      setIsLoading(false);
      return;
    }

    const runAsync = async () => {
      const queries = qs.parse(location.search, { ignoreQueryPrefix: true, comma: true }) || {};
      const {
        code,
        organizationCode: organizationCodeQuery,
        redirect,
        token: tokenQuery,
        userCode,
        ...restQueries
      } = queries;

      const tokenStorage = storage.getUserToken();
      let token = tokenQuery || tokenStorage;

      if (code && userCode) {
        const response = await sendCode({ code, userCode });
        token = response.token;
      }

      if (token) {
        //! CASE A: if there is a token in the url or in the storage

        //! CASE A1: check if the token is valid
        const [_header, payload, _signature] = token.split('.');
        const decodedPayload = JSON.parse(atob(payload));

        //! CASE A2: if the token is valid, then set the token in the storage
        storage.setUserToken(token, {
          expires: new Date(decodedPayload.exp * 1000)
        });

        //! CASE A21: then refetch the user data
        try {
          await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.useGetUserKey] });
          const { data } = await refetch();
          const { organizations } = data;

          datadogRum.setUser({
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            code: data.code,
            phone: data.phone
          });

          //! CASE A211: check if the user has completed the registration
          let organization = null;

          const organizationCodeStorage = storage.getOrganizationCode();
          const organizationCode = organizationCodeQuery || organizationCodeStorage;
          const localOrganization = organizations.find(item => item.code === organizationCode);

          //! CASE A2111: if there is a organization depending on the
          //! - organization code in the url
          //! - organization code in the storage
          //! then set the organization to the organization variable
          if (localOrganization) organization = localOrganization;
          //! CASE A2112: if there is no organization remove the organization code from the storage
          else storage.removeOrganizationCode();

          //! CASE A21111: it there is no organization in the storage
          if (!organization) {
            //! CASE A21111: if there is just one organization in the user data
            if (organizations.length === 1) {
              await organizationServices.selectOrganization({
                organizationCode: organizations[0].code
              });
              storage.setOrganizationCode(organizations[0].code);
              queryClient.setQueryData(['user'], prev => ({
                ...prev,
                organization: organizations[0]
              }));
            } else {
              //! CASE A21112: if there is more than one organization in the user data
              const newQueries = qs.stringify(
                { ...restQueries, redirect },
                { addQueryPrefix: true }
              );
              navigate(ROUTE_PATHS.SELECT_ORGANIZATION + newQueries);
              return;
            }
          } else {
            //! CASE A21121: if there is an selected organization
            storage.setOrganizationCode(organization.code);
            await queryClient.setQueryData(['user'], prev => ({ ...prev, organization }));
          }

          if (redirect) {
            //! CASE A21112: if there is a redirect in the url
            const redirectUrl = getRedirectUrl(redirect, restQueries);

            navigate(redirectUrl.pathname + redirectUrl.search);
            return;
          } else if ((code && userCode) || tokenQuery) {
            //! CASE A21113: If the user has the code and userCode params in the url, then redirect to the home page
            navigate(ROUTE_PATHS.HOME);
            return;
          }
        } catch (error) {
          showError({
            title: 'Oops!',
            message: error.message
          });
        }

        storage.removeSsoLogin();

        sendPostMessage({
          type: 'auth-success',
          token: token
        });

        storage.updateSsoLogin(true);

        if (NON_REDIRECT_PATHS.includes(location.pathname)) {
          navigate(ROUTE_PATHS.HOME);
        }
      } else {
        //! CASE B: if there is no token in the url or in the storage
        // const isPublicLogin = configs.publicLogin.enabled;

        // if (!isPublicLogin) {
        //   navigate(ROUTE_PATHS.NOT_FOUND);
        //   return;
        // }

        //! CASE B1: if it is just '/'
        if (window.location.pathname === '/') {
          navigate(redirectPage, { replace: true });
          return;
        }

        //! CASE B2: if there is no token in the url or in the storage and the current path is not in the NON_REDIRECT_PATHS
        if (NON_REDIRECT_PATHS.includes(location.pathname)) {
          return;
        }

        //! CASE B3: if CASE B1 and CASE B2 are not true, then redirect to the sign up page with the current url as the redirect query
        const queryString = qs.stringify(
          { redirect: window.location.href },
          { addQueryPrefix: true }
        );
        const navigateTo = `${redirectPage}${queryString}`;
        navigate(navigateTo, { replace: true });
        return;
      }
    };

    try {
      await runAsync();
    } catch (error) {
      navigate(redirectPage, { replace: true });
    } finally {
      setIsLoading(false);
    }
  }

  const isPunchoutSession = queries[PUNCH_OUT_SESSION];

  useEffect(() => {
    if (!isPunchoutSession) {
      startAuth();
    }
  }, []);

  if (isPunchoutSession) {
    return (
      <ContinueLogin
        startAuth={() => {
          startAuth();
        }}
      />
    );
  }

  return isLoading ? <AppLoader /> : children;
}
